import { MobileDocumentField } from '../../backbone/features/mobileForm/documentField/models';
import { MobileMediaField } from '../../backbone/shared/models/MobileMediaField';
import { isWeb } from '../../shared/utils/is';
import { showErrorMessage } from '../../shared/utils/notifications';
import { BaseZenputCollection, BaseZenputModel, Paginator } from '../shared/forms';
import announcement_utils from './announcements';

export const AnnouncementCreate = BaseZenputModel.extend({
    defaults: {
        message: '',
        title: '',
        distribute_groups: null,
        users: null,
        user_roles: null,
        attachments: null,
        date_scheduled: null
    },

    url: '/api/v2/announcements/',
    _send_to: null,

    initialize: function () {
        this.set('distribute_groups', []);
        this.set('users', []);
        this.set('user_roles', []);
        this.set('attachments', []);

        AnnouncementCreate.__super__.initialize.apply(this, arguments);
    },

    _get_model_prop: function () {
        var pair_keys = {
            user: 'users',
            role: 'user_roles',
            team: 'distribute_groups'
        };
        return pair_keys[this._send_to];
    },

    _validateSendTo: function (value, attr, attrs) {
        if (attr !== this._get_model_prop()) {
            return false;
        }
        if (_.isEmpty(value)) {
            return _t('mobile_table_of_contents_required');
        }
    },
    _validateDateScheduled: function (value, attr, attrs) {
        if (_.isNull(value)) {
            return;
        }

        var difference_minutes = Math.abs(moment.duration(moment().diff(value)).asMinutes());

        if (difference_minutes < $f.mobile.announcements.date_scheduled.validationOffset) {
            return _t('mobile_scheduled_announcements_validation_error', {
                date: moment()
                    .add($f.mobile.announcements.date_scheduled.validationOffset, 'minutes')
                    .format($f.utils.ANNOUNCEMENT_DATE_AND_TIME_FMT)
            });
        }
    },
    _validateAttachments: function (value, attr, attrs) {
        const MAX_ATTACHMENTS = 20;
        if (attrs.attachments.length > MAX_ATTACHMENTS) {
            var err = _t('mobile_announcement_attachment_validation_error', {
                maxCount: MAX_ATTACHMENTS
            });
            showErrorMessage(err, null, 3000);
            return err;
        }
    },

    validation: {
        attachments: {
            fn: '_validateAttachments'
        },
        title: _.extend(announcement_utils.title, {
            required: true
        }),
        message: _.extend(announcement_utils.message, {
            required: true
        }),
        distribute_groups: {
            fn: '_validateSendTo'
        },
        users: {
            fn: '_validateSendTo'
        },
        user_roles: {
            fn: '_validateSendTo'
        },
        date_scheduled: {
            fn: '_validateDateScheduled'
        }
    }
});

export const AnnouncementAttachment = BaseZenputModel.extend({
    defaults: {
        path: null,
        file_name: null,
        content_type: null,
        file_size: null,
        path_small: null
    }
});

export const AnnouncementAttachments = BaseZenputCollection.extend({
    model: AnnouncementAttachment
});

export const MediaField = MobileMediaField.extend({
    get_parent_upload_guid: function () {
        return this.parent_upload_guid;
    },

    create_field_upload_filename: function (file_entry) {
        return $f.storage.create_s3_filepath($f.active_company.id, 'announcements', null, file_entry.name);
    },

    get_attachments: function () {
        var files = this.get('file') || [];

        return files.map(function (file) {
            const raw = file.get('raw_file');
            const original_name = raw.name;
            let content_type;

            if ($f.flags.get_flag('get_content_type_from_raw_file')) {
                if (isWeb()) {
                    content_type = raw.type;
                } else {
                    content_type = file.get('mimetype');
                }
            } else {
                content_type = file.get('mimetype');
            }
            return {
                content_type: content_type,
                file_name: original_name,
                file_size: file.get('size'),
                path: file.get('path')
            };
        });
    }
});

export const DocumentFieldForAnnouncement = MobileDocumentField.extend({
    get_parent_upload_guid: function () {
        return this.parent_upload_guid;
    },

    create_field_upload_filename: function (file_entry) {
        return $f.storage.create_s3_filepath($f.active_company.id, 'announcements', null, file_entry.name);
    },

    get_attachments: function () {
        var files = this.get('file') || [];

        return files.map(function (file) {
            const raw = file.get('raw_file');
            const original_name = isWeb() ? raw.name : file.get('metadata').name;
            let content_type;

            if ($f.flags.get_flag('get_content_type_from_raw_file')) {
                if (isWeb()) {
                    content_type = raw.type;
                } else {
                    content_type = file.get('mimetype');
                }
            } else {
                content_type = file.get('mimetype');
            }
            return {
                content_type: content_type,
                file_name: original_name,
                file_size: file.get('size'),
                path: file.get('path')
            };
        });
    }
});

export const Announcement = BaseZenputModel.extend({
    defaults: {
        attachments: [],
        user_roles: [],
        distribute_groups: [],
        users: [],
        date_created: null,
        date_scheduled: null,
        date_sent: null,
        message: null,
        opened_count: null,
        recipients_count: null,
        title: null
    },

    url: function () {
        return `/api/v2/announcements/${this.get('id')}/`;
    },

    get_sent_to_list: function () {
        var user_roles = _.pluck(this.get('user_roles'), 'name');
        var distribute_groups = _.pluck(this.get('distribute_groups'), 'name');
        var users = _.pluck(this.get('users'), 'display_name');
        return _.extend([], user_roles, distribute_groups, users);
    }
});

export const Announcements = BaseZenputCollection.extend({
    paginator: null, // Paginator
    model: Announcement,
    status: 'sent',
    order_by: '-date_created',

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);
        Announcements.__super__.initialize.apply(this, arguments);
        this.paginator = new Paginator({
            start: 0,
            limit: 20,
            order_by: options.order_by
        });
        this.status = options.status;
    },

    url: function () {
        return `/api/v2/announcements/?start=${this.paginator.get('start')}&limit=${this.paginator.get(
            'limit'
        )}&order_by=${this.paginator.get('order_by')}&status=${this.status}`;
    }
});

export const AnnouncementRecipients = BaseZenputCollection.extend({
    paginator: null, // Paginator
    is_read: false, // Toggle for opened<true> | unopened<false>
    announcement_model: null, // Announcement

    initialize: function (models, options) {
        AnnouncementRecipients.__super__.initialize.apply(this, arguments);

        this.is_read = options.is_read;
        this.announcement_model = options.announcement_model;

        this.paginator = new Paginator({
            start: 0,
            limit: 20,
            order_by: '-date_seen'
        });
    },

    url: function () {
        return `/api/v2/announcements/${this.announcement_model.get('id')}/recipients/?read=${
            this.is_read
        }&start=${this.paginator.get('start')}&limit=${this.paginator.get('limit')}&order_by=${this.paginator.get(
            'order_by'
        )}`;
    }
});

export const ResendRecipients = BaseZenputModel.extend({
    announcement_model: null, // Announcement

    initialize: function (models, options) {
        this.announcement_model = options.announcement_model;

        ResendRecipients.__super__.initialize.apply(this, arguments);
    },

    url: function () {
        return `/api/v2/announcements/${this.announcement_model.get('id')}/notifications/`;
    }
});

export const ExpectedAnnouncementRecipients = BaseZenputCollection.extend({
    announcement_model: null, // Announcement or CreateAnnouncement

    initialize: function (models, options) {
        this.announcement_model = options.announcement_model;
        ExpectedAnnouncementRecipients.__super__.initialize.apply(this, arguments);
    },

    url: function () {
        var pair_keys = {
            users: 'user_ids',
            user_roles: 'user_role_ids',
            distribute_groups: 'distribute_group_ids'
        };
        var recipient_kind = 'distribute_groups';
        if (this.announcement_model.get('user_roles').length != 0) {
            recipient_kind = 'user_roles';
        } else if (this.announcement_model.get('users').length != 0) {
            recipient_kind = 'users';
        }

        const ids = this.announcement_model.get(recipient_kind).map((kind) => kind.id);

        return `/api/v2/announcements/recipients/?${pair_keys[recipient_kind]}=${ids.join(',')}`;
    }
});

export const Banner = BaseZenputModel.extend({
    default: {
        id: null,
        attachments: null,
        created_user: '',
        title: '',
        message: '',
        date_created: null,
        type: '',
        message_url: '',
        label: ''
    }
});

export const Banners = BaseZenputCollection.extend({
    url: '/api/v3/banners/',
    model: Banner,
    cache_enabled: true,
    cache_key: 'banners',
    cache_ttl: 300000, // 5 mins
    paginator: null,
    initialize: function (models, options) {
        Banners.__super__.initialize.apply(this, arguments);
        this.paginator = new Paginator();
    }
});
